"use client";

import { useContext, useState } from "react";
import { RatingCard } from "@/components/doctor-training/rating-card";
import { SeatsCard } from "@/components/doctor-training/seats-card";
import { createPath } from "@/service/routing/routing";
import { ResourceContext } from "@/contexts/resource-context";
import { clientGet } from "@/service/client-api/client-api";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ChevronDownIcon, Trash2Icon } from "lucide-react";

export const CardResidencyDetails = ({
  details,
  facility,
  collapsed,
}: {
  details: MedicalFacilityCardResidencyDetails;
  facility: MedicalFacilityCard;
  collapsed: boolean;
}) => {
  const { unitGroups } = details;
  return (
    <div>
      {unitGroups.map(({ specialization, units }, index) => (
        <UnitGroup
          key={specialization.slug}
          specialization={specialization}
          units={units}
          facility={facility}
          collapsed={collapsed}
          index={index}
        />
      ))}
    </div>
  );
};

function UnitGroup({
  specialization,
  units,
  facility,
  collapsed,
  index,
}: {
  specialization: MedicalFacilityUnitGroupSpecialization;
  units: MedicalFacilityUnitCard[];
  facility: MedicalFacilityCard;
  collapsed: boolean;
  index: number;
}) {
  const { favouriteList } = useContext(ResourceContext) as ResourceInfo;
  const [showUnits, setShowUnits] = useState(!collapsed || units.length === 1);

  const inactive = units.length === 1;

  return (
    <div key={specialization.slug}>
      <div
        className={cn(
          "mb-4 flex items-center justify-between",
          inactive ? "hover:cursor-initial" : "hover:cursor-pointer",
          index !== 0 && "mt-6",
        )}
        onClick={() => {
          if (!collapsed || units.length === 1) {
            return;
          }

          setShowUnits(!showUnits);
        }}
      >
        <Badge className={"bg-[#e0ebff] text-[#122258] opacity-100"}>
          {specialization.title}
        </Badge>
        {collapsed && units.length > 1 && (
          <div
            className={cn(
              "text-remedium-blue-hover relative flex items-center justify-start gap-2 text-right text-sm leading-tight font-normal text-zinc-950 select-none",
              showUnits && "[&_svg]:transform-[rotate(180deg)]",
            )}
          >
            Oddziały ({units.length}) <ChevronDownIcon size={"16px"} />
          </div>
        )}
      </div>
      {showUnits && (
        <div className={"mx-2"}>
          {units.map(({ title, medicalTrainingRating, seats, slug, id }) => (
            <div key={title} className={"my-4"}>
              <a
                className="text-remedium-blue-hover justify-start text-sm leading-tight font-normal text-zinc-950"
                href={createPath("residency_map.facility.unit.index", {
                  facilitySlug: facility.slug,
                  unitSlug: slug,
                })}
                target={"_blank"}
                rel="noreferrer"
              >
                {title}
              </a>
              <div className={"mx-2 mt-2.5 flex gap-8"}>
                <div className={""}>
                  <SeatsCard seats={seats} />
                </div>

                {medicalTrainingRating.count > 0 ? (
                  <a
                    className={""}
                    href={createPath("residency_map.facility.unit.index", {
                      facilitySlug: facility.slug,
                      unitSlug: slug,
                    })}
                    target={"_blank"}
                    rel="noreferrer"
                  >
                    <RatingCard rating={medicalTrainingRating} />
                  </a>
                ) : (
                  <RatingCard rating={medicalTrainingRating} />
                )}

                {favouriteList && (
                  <div
                    className={"text-rose-600 hover:cursor-pointer"}
                    onClick={async () => {
                      await clientGet("favourite", {
                        resourceType: "medical_facility_unit",
                        resourceId: id,
                        action: "remove",
                      });
                      window.location.reload();
                    }}
                  >
                    <Trash2Icon size={"16px"} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
